import Image from 'next/image';
import NextLink from 'next/link';
import { oneLine } from 'common-tags';

import { Accordion, Link, RichInline } from '@/atoms';
import { LinkVariant } from '@/atoms/link/types';

import type { FunctionComponent } from 'react';
import type { TypeFaqBlockProps } from './types';

/**
 * FAQ composition del Figma HoM (foto + máscara + accordions card).
 * Reutilizado desde el dynamic component `ComponentRevisadasPreguntasFrequentes`
 * y desde el `Prefooter` (sin duplicar).
 */
export const FaqBlock: FunctionComponent<TypeFaqBlockProps> = ({
  className = '',
  eyebrow,
  title,
  subtitle,
  image,
  imageShapeUrl,
  accordions,
  link,
  eyebrowStyleClass,
  titleStyleClass,
  subtitleStyleClass,
  accordionTitleStyleClass,
  accordionDescriptionStyleClass,
  linkStyleClass,
}: TypeFaqBlockProps) => (
  <div
    className={`grid grid-cols-1 items-start gap-8 lg:gap-20 ${image?.src ? 'lg:grid-cols-2' : ''} ${className}`}
  >
    {image?.src && (
      <div className="relative hidden aspect-[760/650] w-full lg:block">
        <div
          className="absolute inset-0 overflow-hidden"
          style={
            imageShapeUrl
              ? {
                  WebkitMaskImage: `url(${imageShapeUrl})`,
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  WebkitMaskSize: '100% 100%',
                  maskImage: `url(${imageShapeUrl})`,
                  maskRepeat: 'no-repeat',
                  maskPosition: 'center',
                  maskSize: '100% 100%',
                }
              : undefined
          }
        >
          <Image
            src={image.src}
            alt={image.alt ?? ''}
            fill
            sizes="(max-width: 1024px) 100vw, 50vw"
            className="object-cover"
          />
        </div>
      </div>
    )}

    <div className="flex flex-col gap-6">
      {(eyebrow || title) && (
        // En móvil (1 col) el cluster eyebrow+título va flush-left para alinear
        // con los acordeones de abajo; en lg+ (grid 2 col) recupera el
        // right-align "colgado del gutter" del diseño HoM.
        <div className="flex flex-col items-start self-start lg:items-end">
          {eyebrow && (
            <RichInline
              html={eyebrow}
              className={oneLine`font-accent text-primary-500 ${
                eyebrowStyleClass ?? 'text-3xl leading-10 font-normal'
              }`}
            />
          )}
          {title && (
            <RichInline
              as="h2"
              html={title}
              className={oneLine`text-headline text-text-primary font-primary ${
                titleStyleClass ?? 'text-headline-h1 font-bold'
              }`}
            />
          )}
        </div>
      )}

      {subtitle && (
        <RichInline
          as="p"
          html={subtitle}
          className={oneLine`text-body text-text-primary ${
            subtitleStyleClass ?? 'text-body-l'
          }`}
        />
      )}

      <div className="flex flex-col gap-3">
        {accordions.map((itm, index) => (
          <Accordion
            key={`faq-${index}`}
            title={itm.title}
            variant="normal"
            color="card"
            titleStyleClass={accordionTitleStyleClass}
            descriptionStyleClass={accordionDescriptionStyleClass}
          >
            {itm.description}
          </Accordion>
        ))}
      </div>

      {link && (
        <NextLink href={link.href} prefetch={false} passHref legacyBehavior>
          <Link
            icon="right"
            variant={LinkVariant.POSITIVE}
            className={oneLine`font-bold ${linkStyleClass ?? ''}`}
          >
            {link.label}
          </Link>
        </NextLink>
      )}
    </div>
  </div>
);

FaqBlock.displayName = 'FaqBlock';
